import * as React from 'react';
import classNames from 'classnames';
import deepmerge from 'deepmerge';

import type { ColorStyleProps } from '../../../@types/theme';
import { useTheme } from '../../../contexts/theme';
import Subtitle from '../../atoms/Subtitle';
import Text from '../../atoms/Text';
import Spinner from '../../atoms/Spinner';

import styles from './LinearGaugeChart.module.scss';

// Interfaces
// //////////////////////////

export interface GaugeOptions {
  colors: Array<{
    threshold?: number;
    barColor: ColorStyleProps | `#${string}`;
    color: ColorStyleProps | `#${string}`;
  }>;
  axisLeft: {
    className?: string;
  };
  axisRight: {
    key?: string;
    className?: string;
    enabled: true | boolean;
  };
  axisBottom: {
    min: number;
    max: number;
  };
  /** This option is not current available */
  displayTicks: boolean;
}

// This should be any for generic data
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface GaugeData extends Record<string, any> {
  id: string;
  value: 0 | number | null;
  barColor?: ColorStyleProps | `#${string}`;
  color?: ColorStyleProps | `#${string}`;
}

export interface Props extends React.HTMLAttributes<HTMLElement> {
  loading?: boolean;
  data: Array<GaugeData>;
  options?: Partial<GaugeOptions>;
}

/**
 * Default chart options
 */
const defaultOptions: GaugeOptions = {
  colors: [
    { threshold: 80, barColor: 'green400', color: 'black' },
    { threshold: 70, barColor: 'yellow400', color: 'black' },
    { barColor: 'red400', color: 'white' },
  ],
  axisLeft: {},
  axisRight: {
    enabled: false,
  },
  axisBottom: {
    min: 0,
    max: 100,
  },
  displayTicks: false,
};

/**
 * Retrieves colors for this component based on options config
 */
const getColor = (
  key: 'color' | 'barColor',
  data: GaugeData,
  options: GaugeOptions,
  themeColors: Record<ColorStyleProps, string | string[]>,
) => {
  const { value } = data;
  const color = data[key];
  if (value === null) return 'transparent';
  if (color) {
    return color.startsWith('#')
      ? color
      : (themeColors[color as ColorStyleProps] as string);
  }
  const colorMatch = options.colors.find(
    (c) =>
      (c.threshold !== undefined && value >= c.threshold) ||
      c.threshold === undefined,
  );
  if (!colorMatch) return 'transparent';
  return colorMatch[key].startsWith('#')
    ? colorMatch[key]
    : (themeColors[colorMatch[key] as ColorStyleProps] as string);
};

/**
 * <LinearGaugeChart> component
 */
export const LinearGaugeChart: React.FunctionComponent<Props> = ({
  className = '',
  data,
  loading,
  options = {},
  ...htmlElementProps
}: Props) => {
  const { theme } = useTheme();

  const opts: GaugeOptions = React.useMemo(
    () => ({
      ...deepmerge(defaultOptions, options),
      // Colors array is being appended by the deepmerge function
      // which is not what we want
      colors: options.colors ? options.colors : defaultOptions.colors,
    }),
    [options],
  );

  return (
    <div
      className={classNames(styles.wrapper, className, {
        [styles.loading]: loading,
      })}
      {...htmlElementProps}
    >
      {loading && <Spinner data-testid="linear-gauge-spinner" />}

      {!loading && (
        <div
          className={classNames('linear-gauge-chart', styles['chart-wrapper'])}
          data-testid="chart-wrapper"
        >
          {data.map((d) => (
            <div key={d.id} className={styles['row-wrapper']}>
              {/* Label */}
              <div
                className={classNames(styles.label, opts.axisLeft.className)}
              >
                <Subtitle size="sm2" className="ellipsize">
                  {d.id}
                </Subtitle>
              </div>

              <div className={styles.track}>
                {/* Empty track */}
                {(d.value === null ||
                  d.value === undefined ||
                  d.value === 0) && (
                  <div className="p-2">
                    <Text size="sm1" weight="bold">
                      {d.value === 0 ? d.value : '-'}
                    </Text>
                  </div>
                )}

                {/* Progress bar */}
                {d.value !== null && d.value !== undefined && d.value !== 0 && (
                  <div
                    className={classNames(styles.progress, {
                      positive: d.value > 0,
                    })}
                    style={{
                      width: `${Math.min(
                        Math.abs((d.value * 100) / opts.axisBottom.max),
                        100,
                      )}%`,
                      backgroundColor: getColor(
                        'barColor',
                        d,
                        opts,
                        theme.color,
                      ),
                      color: getColor('color', d, opts, theme.color),
                    }}
                  >
                    <Text size="sm1" weight="bold">
                      {d.value}
                    </Text>
                  </div>
                )}
              </div>

              {/* Additional text */}
              {opts.axisRight.enabled && opts.axisRight.key && (
                <div
                  className={classNames(
                    styles.compl,
                    'ellipsize',
                    opts.axisLeft.className,
                  )}
                >
                  <Subtitle size="sm2">{d[opts.axisRight.key]}</Subtitle>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LinearGaugeChart;
